import * as React from 'react';
import * as styles from './AuctionBetManager.css';
import { Button } from '@cian/button-component';
import * as PropTypes from 'prop-types';
import { AuctionBetInput } from '../AuctionBetInput';
import {
  trackAuctionHint,
  trackAuctionChangeBetClick,
  trackAuctionSendBetClick,
  trackAuctionNotEnoughMoneyPopupShow,
  trackAuctionErrorPopupShow,
  trackAuctionErrorPopupRetry,
  trackAuctionNotEnoughMoneyPopupPayClick,
} from './tracking';
import { setOfferAuctionBet, TSetAuctionBetStatus } from '../../../api/setOfferAuctionBet';
import { AuctionReduceBetModal } from '../AuctionReduceBetModal';
import { keepValueInRange, getNearestUpperMultiple } from '@cian/utils';
import { getApplyNewBetDate } from '../../../api/getApplyNewBetDate';

export interface IAuctionBetManagerProps {
  initialBet: number;
  isFromOwn: boolean;
  onClose(): void;
  auctionBetCalculator(value: number | null): number;
}

export interface IAuctionBetManagerState {
  bet: number;
  isPlacingBet: boolean;
  isBetValid: boolean;
  isNotificationShow: boolean;
  isReducingBet: boolean;
}

export interface IAuctionStatusNotificationProps {
  status: TSetAuctionBetStatus;
  onClose?(): void;
  onRetryClick?(): void;
  onPayClick?(): void;
}

export class AuctionBetManager extends React.Component<IAuctionBetManagerProps, IAuctionBetManagerState> {
  public static contextTypes = {
    httpApi: PropTypes.object,
    offer: PropTypes.object,
    maxAuctionBet: PropTypes.number,
    auctionMinBet: PropTypes.number,
    auctionMaxBet: PropTypes.number,
    validateStep: PropTypes.bool,
  };

  public constructor(props: IAuctionBetManagerProps) {
    super(props);

    this.state = {
      bet: props.initialBet,
      isPlacingBet: false,
      isBetValid: true,
      isNotificationShow: false,
      isReducingBet: false,
    };
  }

  public componentWillMount() {
    const { auctionMaxBet, validateStep, offer } = this.context;
    const { initialBet } = this.props;

    const stepBet = offer.auction ? offer.auction.stepBet : 1;

    const newBet = validateStep
      ? getNearestUpperMultiple(initialBet + 1, stepBet) || 0
      : initialBet + 1;

    this.setState({ bet: keepValueInRange(newBet, 0, auctionMaxBet) });
  }

  public render() {
    const { offer, validateStep } = this.context;
    const { isFromOwn, onClose, auctionBetCalculator } = this.props;
    const { bet, isPlacingBet, isBetValid, isReducingBet } = this.state;
    const isSubmitButtonShow = bet !== offer.auction.currentBet;

    return (
      <div className={styles['auction_manage_bet']}>
        <div className={styles['raise_bet_image']} />
        <div>
          {isFromOwn ? (
            <>
              Хотите поднять объявление<br />
              на первую страницу поиска?<br />
              Повысьте ставку:
            </>
          ) : (
            <>
              Хотите опередить объявление<br />
              конкурента? Повысьте ставку:
            </>
          )}
          <div className={styles['raise_bet_wrapper']}>
            <div className={styles['input_wrapper']}>
              <AuctionBetInput
                stepBet={offer.auction ? offer.auction.stepBet : 1}
                validateStep={validateStep}
                initialBet={bet}
                onChange={this.handleBetChange}
                trackChangeBetClick={() => trackAuctionChangeBetClick(offer)}
                auctionBetCalculator={auctionBetCalculator}
              />
            </div>

            {isSubmitButtonShow &&
              <Button
                onClick={this.handleBetSubmit}
                disabled={!isBetValid || isPlacingBet}
                isSync={isPlacingBet}
              >
                Ок
              </Button>
            }
          </div>
        </div>

        {isReducingBet &&
          <AuctionReduceBetModal
            validateStep={validateStep}
            stepBet={offer.auction ? offer.auction.stepBet : 1}
            bet={bet}
            onCancel={onClose}
            onSubmit={this.setOfferBet}
            auctionBetCalculator={auctionBetCalculator}
          />
        }
      </div>
    );
  }

  private handleBetChange = (bet: number, isBetValid: boolean) => {
    this.setState({ bet, isBetValid });
  }

  private handleBetSubmit = () => {
    const { offer } = this.context;
    const { bet } = this.state;
    const isReducingBet = bet < offer.auction.currentBet;

    this.setState({ isPlacingBet: !isReducingBet, isReducingBet });

    trackAuctionHint('up_search', 'click', offer);

    if (!isReducingBet) {
      this.setOfferBet(bet);
    }
  }

  private setOfferBet = (bet: number) => {
    const { httpApi, offer } = this.context;
    const { isReducingBet } = this.state;

    const event = new Event('auctionNotificationHide');
    document.dispatchEvent(event);

    setOfferAuctionBet(httpApi, offer.id, bet)
      .then(({ status }) => {
        const { onClose } = this.props;

        let props: IAuctionStatusNotificationProps = { status };

        switch (status) {
          case 'completed':
            if (isReducingBet) {
              getApplyNewBetDate(httpApi, offer.id)
                .then(({ applyNewBetDate, nextBet }) => {
                  offer.auction.applyNewBetDate = applyNewBetDate;
                  offer.auction.nextBet = nextBet;

                  this.triggerRerender();
                });
            } else {
              offer.auction.currentBet = bet;
              offer.auction.applyNewBetDate = undefined;
              offer.auction.nextBet = bet;

              this.triggerRerender();
            }

            trackAuctionSendBetClick(offer, bet);

            this.showNotification(props);

            onClose();

            break;

          case 'failed':
            props = this.getFailedState();

            this.showNotification(props);
            this.setState({ isPlacingBet: false, isReducingBet: false });

            break;

          case 'notEnoughMoney':
            props = {
              status,
              onPayClick: this.handlePayClick,
            };

            this.showNotification(props);
            this.setState({ isPlacingBet: false, isReducingBet: false });

            trackAuctionNotEnoughMoneyPopupShow();

            break;

          default:
            this.showNotification(props);

            onClose();
        }
      })
      .catch(() => {
        const props = this.getFailedState();

        this.showNotification(props);

        this.setState({ isPlacingBet: false, isReducingBet: false });
      });
  }

  private getFailedState = (): IAuctionStatusNotificationProps => {
    trackAuctionErrorPopupShow();

    return {
      status: 'failed',
      onRetryClick: this.handleRetryClick,
    };
  }

  private handleRetryClick = () => {
    trackAuctionErrorPopupRetry();

    this.handleBetSubmit();
  }

  private handlePayClick = () => {
    trackAuctionNotEnoughMoneyPopupPayClick();

    location.href = 'https://my.cian.ru/pay';
  }

  private showNotification = (detail: IAuctionStatusNotificationProps) => {
    const event = new CustomEvent('auctionNotificationShow', { detail });

    document.dispatchEvent(event);
  }

  private triggerRerender = () => {
    const { offer } = this.context;
    const detail = { offerId: offer.id };
    const event = new CustomEvent('auctionRightPanelRerender', { detail });

    document.dispatchEvent(event);
  }
}
